import { useEffect, useRef, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { Star } from 'lucide-react'
import type { CameraSphereMode, ChangeCameraMode } from './useExpandedCamera'
import './camera-sphere.css'

export const HOVER_OPEN_MS = 120
export const HOVER_CLOSE_MS = 250
/** After the full view closes under a mouse that is elsewhere, the preview tucks back in. */
export const RETURN_CLOSE_MS = 1_200

/** How recent the camera's location is. Surfaces without fix times leave it out. */
export type CameraFreshness = {
  /** "Location 4s ago": shown in the preview and read to assistive tech. */
  label: string
  /** Full context for assistive tech when the visible label is only a relative time. */
  accessibleLabel?: string
  /** "40s ago": the rim tag on a stale camera. */
  age: string
  stale: boolean
}

export type CameraSphereProps = {
  id: string
  name: string
  /** The surface's own video for this camera: a LiveKit surface, a still or initials. */
  video: ReactNode
  mode: CameraSphereMode
  /** 'full' is a request: surfaces with their own camera player open it instead. */
  onModeChange: ChangeCameraMode
  /** Video is flowing. Only this earns the green rim dot. */
  live?: boolean
  /** Short state for a camera that is not live ("Offline", "Reconnecting"). */
  status?: string
  freshness?: CameraFreshness
  selected?: boolean
  /** Publicly featured: independent of selection and of live. */
  featured?: boolean
  /** Nothing to preview or open. */
  disabled?: boolean
  /** A key the map flies to this camera with (the play world's 1–9), announced to assistive tech. */
  shortcut?: string
  size?: 'md' | 'sm'
}

const COMPACT_QUERY = '(max-width: 720px)'
const SIZES = {
  md: { wide: { orb: 84, peek: [240, 135] }, compact: { orb: 72, peek: [200, 113] } },
  sm: { wide: { orb: 60, peek: [208, 117] }, compact: { orb: 52, peek: [176, 99] } },
} as const

function useCompact() {
  const query = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(COMPACT_QUERY).matches)
  const [compact, setCompact] = useState(query)
  useEffect(() => {
    const list = window.matchMedia?.(COMPACT_QUERY)
    if (!list) return
    const update = () => setCompact(list.matches)
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [])
  return compact
}

function keyboardFocus(element: Element) {
  try {
    return element.matches(':focus-visible')
  } catch {
    return false
  }
}

/**
 * A camera anchored at its fix on any map; the tail points at the fix and
 * never moves. Two states on every map: hover, keyboard focus or a first tap
 * grows the circle into a 16:9 preview upward from the tail; a click, Enter or
 * a second tap asks to open the camera.
 */
export function CameraSphere({
  id,
  name,
  video,
  mode,
  onModeChange,
  live = false,
  status,
  freshness,
  selected = false,
  featured = false,
  disabled = false,
  shortcut,
  size = 'md',
}: CameraSphereProps) {
  const reducedMotion = useReducedMotion()
  const compact = useCompact()
  const timer = useRef<number | undefined>(undefined)
  const pointer = useRef<string>('mouse')
  const hovering = useRef(false)
  const previous = useRef(mode)
  const peek = mode !== 'orb'
  const stale = Boolean(freshness?.stale)

  const clear = () => {
    window.clearTimeout(timer.current)
    timer.current = undefined
  }
  const later = (next: CameraSphereMode, ms: number, onlyFrom: CameraSphereMode[]) => {
    clear()
    timer.current = window.setTimeout(() => onModeChange(id, next, onlyFrom), ms)
  }
  useEffect(() => clear, [])
  useEffect(() => {
    if (previous.current === 'full' && mode === 'peek' && pointer.current === 'mouse' && !hovering.current) later('orb', RETURN_CLOSE_MS, ['peek'])
    previous.current = mode
  }, [mode])

  const dims = SIZES[size][compact ? 'compact' : 'wide']
  const frame = peek
    ? { width: dims.peek[0], height: dims.peek[1], borderRadius: 16 }
    : { width: dims.orb, height: dims.orb, borderRadius: dims.orb / 2 }
  const transition = reducedMotion ? { duration: 0 } : { type: 'spring' as const, stiffness: 460, damping: 30, mass: 0.7 }
  const state = live ? 'live' : (status ?? 'not live').toLowerCase()
  const label = [`${name}, ${state}`, selected && 'selected', featured && 'featured'].filter(Boolean).join(', ') + (freshness ? `. ${freshness.accessibleLabel ?? freshness.label}` : '')
  const detail = freshness && !stale ? freshness.label : !live ? status : undefined

  return (
    <motion.div
      className={['cs-sphere', `cs-sphere--${size}`, stale && 'cs-sphere--stale', selected && 'cs-sphere--selected', !live && 'cs-sphere--idle'].filter(Boolean).join(' ')}
      data-mode={mode}
      initial={reducedMotion ? false : { opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: [0.2, 0.7, 0.2, 1] }}
      onPointerEnter={(event) => {
        pointer.current = event.pointerType
        if (event.pointerType !== 'mouse' || disabled) return
        hovering.current = true
        if (mode === 'orb') later('peek', HOVER_OPEN_MS, ['orb'])
        else clear()
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse' || disabled) return
        hovering.current = false
        if (mode === 'peek') later('orb', HOVER_CLOSE_MS, ['peek'])
        else clear()
      }}
    >
      <button
        type="button"
        className="cs-sphere__hit"
        data-camera-sphere={id}
        aria-label={label}
        aria-expanded={disabled ? undefined : peek}
        aria-keyshortcuts={shortcut}
        disabled={disabled}
        onPointerDown={(event) => {
          pointer.current = event.pointerType
        }}
        onFocus={(event) => {
          if (mode === 'orb' && keyboardFocus(event.currentTarget)) {
            clear()
            onModeChange(id, 'peek', ['orb'])
          }
        }}
        onBlur={() => {
          if (pointer.current !== 'mouse' || !hovering.current) later('orb', HOVER_CLOSE_MS, ['peek'])
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && mode === 'peek') {
            clear()
            onModeChange(id, 'orb', ['peek'])
          }
        }}
        onClick={() => {
          clear()
          onModeChange(id, pointer.current !== 'mouse' && mode === 'orb' ? 'peek' : 'full')
        }}
      >
        <span className="cs-sphere__body">
          <motion.span className="cs-sphere__frame" data-camera-frame={id} initial={false} animate={frame} transition={transition}>
            {video}
          </motion.span>
          <span className={`cs-sphere__dot ${live ? 'cs-sphere__dot--live' : ''}`} aria-hidden />
          {featured && (
            <span className="cs-sphere__featured" aria-hidden>
              <Star size={11} fill="currentColor" strokeWidth={2} />
            </span>
          )}
          {stale && freshness && <span className="cs-sphere__age">{freshness.age}</span>}
        </span>
        <span className="cs-sphere__name">
          <strong>{name}</strong>
          {peek && detail && <span>{detail}</span>}
        </span>
        <span className="cs-sphere__tail" aria-hidden />
      </button>
    </motion.div>
  )
}
